import { Link } from 'react-router-dom';
import AdBanner from '../../components/AdBanner';
import { AD_SLOTS } from '../../config/adSlots';
import { useDocumentMeta } from '../../hooks/useDocumentMeta';
import { useStructuredData } from '../../hooks/useStructuredData';
import BlogArticleLayout from './BlogArticleLayout';

export default function NcaaWrestlingScoringArticle() {
  useDocumentMeta(
    'NCAA Wrestling Scoring Rules Explained Simply',
    'A straightforward breakdown of how NCAA wrestling tournament scoring works — placement points, advancement points, bonus points, and how they add up.'
  );

  useStructuredData({
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: 'NCAA Wrestling Scoring Rules Explained Simply',
    description:
      'A straightforward breakdown of how NCAA wrestling tournament scoring works — placement points, advancement points, bonus points, and how they add up.',
    datePublished: '2026-03-03',
    author: { '@type': 'Organization', name: 'Takedown League' },
    publisher: {
      '@type': 'Organization',
      name: 'Takedown League',
      url: 'https://takedownleague.com',
    },
    mainEntityOfPage: 'https://takedownleague.com/blog/ncaa-wrestling-scoring-explained',
  });

  return (
    <BlogArticleLayout
      title="NCAA Wrestling Scoring Rules Explained Simply"
      date="2026-03-03"
      readTime="7 min read"
    >
      <p>
        NCAA wrestling tournament scoring can seem confusing at first glance. You see numbers
        climbing on the team scoreboard and it is not always clear where they come from. But the
        system is actually logical once you understand its three components: placement points,
        advancement points, and bonus points. This guide breaks down each one so you can follow
        along with any NCAA tournament — and make smarter picks in your{' '}
        <Link to="/" className="text-purple-600 hover:text-purple-700 underline">
          fantasy wrestling league
        </Link>
        .
      </p>

      <h2>The Three Types of Team Points</h2>
      <p>
        Every wrestler who competes in an NCAA tournament can earn team points in three ways. These
        points are added together to determine the team standings, and in fantasy wrestling, they
        determine your roster's score.
      </p>

      <h2>1. Placement Points</h2>
      <p>
        Placement points are awarded at the end of the tournament based on where a wrestler
        finishes. Only the top eight wrestlers in each weight class earn placement points. The
        breakdown is:
      </p>
      <div className="overflow-x-auto my-4">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="bg-gray-100">
              <th className="border border-gray-300 px-4 py-2 text-left font-semibold">Place</th>
              <th className="border border-gray-300 px-4 py-2 text-left font-semibold">Points</th>
            </tr>
          </thead>
          <tbody>
            {[
              ['1st', '16'],
              ['2nd', '12'],
              ['3rd', '10'],
              ['4th', '9'],
              ['5th', '7'],
              ['6th', '6'],
              ['7th', '4'],
              ['8th', '3'],
            ].map(([place, pts]) => (
              <tr key={place}>
                <td className="border border-gray-300 px-4 py-2">{place}</td>
                <td className="border border-gray-300 px-4 py-2">{pts}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p>
        Placement points are the largest single source of scoring for top wrestlers. A national
        champion earns 16 placement points alone, before counting anything else. This is why top
        seeds are so expensive in fantasy wrestling — their expected placement points are high.
      </p>

      <div className="my-8">
        <AdBanner slot={AD_SLOTS.blog} format="horizontal" />
      </div>

      <h2>2. Advancement Points</h2>
      <p>
        Advancement points are earned each time a wrestler wins a match and moves forward in the
        bracket. They reward depth — a wrestler who competes in many rounds and keeps winning
        accumulates advancement points steadily throughout the tournament.
      </p>
      <p>
        Championship bracket wins earn more advancement points than consolation bracket wins. This
        reflects the higher level of competition on the championship side. A wrestler who stays on
        the championship side of the bracket through the semifinals earns more advancement points
        than one who drops to consolation early, even if both win the same number of matches.
      </p>
      <p>
        Byes also factor in. If a wrestler receives a first-round bye and then wins their next
        match, they earn advancement credit for the bye round as well — 1 point in the championship
        bracket or 0.5 points in consolation. However, a bye followed by a loss earns nothing for
        the bye.
      </p>

      <h2>3. Bonus Points</h2>
      <p>
        Bonus points reward dominant victories. Not all wins are created equal in NCAA wrestling,
        and the scoring system reflects that:
      </p>
      <ul>
        <li>
          <strong>Pin (Fall)</strong> — the highest bonus. Pinning your opponent ends the match
          immediately and earns the maximum bonus points. This is the most decisive win in
          wrestling.
        </li>
        <li>
          <strong>Technical Fall</strong> — winning by 15 or more points triggers a technical fall,
          ending the match early. This earns the second-highest bonus.
        </li>
        <li>
          <strong>Major Decision</strong> — winning by 8 to 14 points. A comfortable margin that
          earns a smaller bonus.
        </li>
        <li>
          <strong>Regular Decision</strong> — winning by fewer than 8 points. No bonus points
          awarded, but the wrestler still earns advancement points.
        </li>
      </ul>
      <p>
        Bonus points make aggressive, offensive wrestlers more valuable in both team scoring and
        fantasy wrestling. A wrestler who pins three opponents generates meaningfully more points
        than one who wins three close decisions.
      </p>

      <h2>How It All Adds Up</h2>
      <p>
        A wrestler's total team point contribution is the sum of their placement points, advancement
        points, and bonus points. Consider a wrestler who wins the national championship by pinning
        two opponents and winning two other matches by decision:
      </p>
      <ul>
        <li>Placement: 16 points (1st place)</li>
        <li>Advancement: points for each match win through the bracket</li>
        <li>Bonus: additional points for the two pins</li>
      </ul>
      <p>
        That wrestler could easily contribute 25+ total team points — a dominant fantasy
        performance. Meanwhile, an eighth-place finisher who lost several close matches might
        contribute only 5-6 total points. The gap between top and bottom is significant, which is
        why seed position matters so much when building your roster.
      </p>

      <h2>What About Team Penalty Deductions?</h2>
      <p>
        In NCAA team scoring, penalties like unsportsmanlike conduct deductions reduce a school's
        overall team total. However, in{' '}
        <Link to="/" className="text-purple-600 hover:text-purple-700 underline">
          Takedown League
        </Link>{' '}
        fantasy scoring, these deductions are not applied to individual wrestlers. Your wrestler's
        fantasy score reflects only the points they earned through competition — placement,
        advancement, and bonus.
      </p>

      <h2>Why Understanding Scoring Matters for Fantasy</h2>
      <p>
        When you draft a fantasy wrestling roster, you are essentially predicting which wrestlers
        will generate the most total team points. Understanding the scoring system helps you see
        beyond just who will win each weight class. A #3 seed who is known for pinning opponents
        might generate more total points than a #2 seed who wins tight decisions. A wrestler in a
        deep, competitive weight class might accumulate extra consolation advancement points even
        without placing.
      </p>
      <p>
        Check out our{' '}
        <Link
          to="/blog/fantasy-wrestling-draft-strategy"
          className="text-purple-600 hover:text-purple-700 underline"
        >
          draft strategy guide
        </Link>{' '}
        to learn how to apply this scoring knowledge when building your roster, and browse{' '}
        <Link to="/tournaments" className="text-purple-600 hover:text-purple-700 underline">
          past tournament results
        </Link>{' '}
        to see how real scoring plays out in practice.
      </p>
    </BlogArticleLayout>
  );
}
